import {
  FEEDBACK_KIND_LABELS,
  FEEDBACK_KINDS,
  type FeedbackKind,
  type FeedbackResultDTO,
} from "@gridaction/core";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, type ReactNode, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Field, Input, Select, Textarea } from "@/components/ui/form";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/overlay";
import { Callout } from "@/components/ui/surface";
import { api, errorMessage } from "@/lib/api";
import { meQuery } from "@/lib/queries";

/** The form itself. It only mounts while the dialog is open, so it costs nothing on other pages. */
function FeedbackForm({
  defaultKind,
  defaultMessage,
  onSent,
}: {
  defaultKind: FeedbackKind;
  defaultMessage: string;
  onSent: () => void;
}) {
  const { data: me } = useQuery({ ...meQuery, retry: false });
  const [kind, setKind] = useState<FeedbackKind>(defaultKind);
  const [message, setMessage] = useState(defaultMessage);
  const [email, setEmail] = useState("");

  const send = useMutation({
    mutationFn: () =>
      api.post<FeedbackResultDTO>("/feedback", {
        kind,
        message: message.trim(),
        email: !me && email.trim() ? email.trim() : undefined,
        page: window.location.pathname,
      }),
    onSuccess: (result) => {
      toast.success(
        result?.ref
          ? `Thanks, we've got it. Your reference is ${result.ref}.`
          : "Thanks, we've got it",
      );
      onSent();
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send.mutate();
  };

  return (
    <form id="send-feedback" onSubmit={submit} className="space-y-4">
      {send.error ? <Callout tone="danger">{errorMessage(send.error)}</Callout> : null}
      <Field label="What is it about?">
        {(props) => (
          <Select
            {...props}
            value={kind}
            onChange={(event) => setKind(event.target.value as FeedbackKind)}
          >
            {FEEDBACK_KINDS.map((option) => (
              <option key={option} value={option}>
                {FEEDBACK_KIND_LABELS[option]}
              </option>
            ))}
          </Select>
        )}
      </Field>
      <Field label="Tell us more" hint="We also see the page you were on and your browser type.">
        {(props) => (
          <Textarea
            {...props}
            rows={5}
            maxLength={2000}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            placeholder="What happened, or what would you like?"
          />
        )}
      </Field>
      {me ? null : (
        <Field label="Your email (optional)" hint="Only if you'd like a reply.">
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="email"
              maxLength={200}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
      )}
      <Button
        type="submit"
        className="w-full"
        loading={send.isPending}
        disabled={message.trim().length < 5}
      >
        Send feedback
      </Button>
    </form>
  );
}

/** Opens the feedback form from any trigger. */
export function FeedbackDialog({
  trigger,
  defaultKind = "broken",
  defaultMessage = "",
  open: controlledOpen,
  onOpenChange,
}: {
  /** What opens the form. Leave it out to open it from outside with `open` and `onOpenChange`. */
  trigger?: ReactNode;
  defaultKind?: FeedbackKind;
  defaultMessage?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = (next: boolean) => {
    setOwnOpen(next);
    onOpenChange?.(next);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent
        title="Send feedback"
        description="GridAction is in beta. Tell us what's broken, what's confusing, or what you'd like to see."
      >
        <FeedbackForm
          defaultKind={defaultKind}
          defaultMessage={defaultMessage}
          onSent={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
